import { ContentState, convertFromRaw } from 'draft-js';
import { CellType, CellValueDetailedType } from 'hyperformula';
import { isBoolean, isNil, isNumber } from 'lodash';
import {
    CellData,
    CellFormulaInfo,
    DateTimeTypeObject,
    HotComputedCellValue,
} from '../../../../../../common/table/TableTypes';
import {
    CellTypeNames,
    DateStringFormatOptions,
    FormatOptions,
    TimeStringFormatOptions,
} from '../../../../../../common/table/CellTypeConstants';
import { getDefaultTypeObject } from '../../../../../../common/table/utils/tableCellTypeUtils';
import { getNumericDateTimeStringFromCellValue } from '../../../../../../common/table/utils/tableInputDateTimeUtils';
import { getRenderCellValueFromNormalisedComputedCellValue } from '../../../../../../common/table/utils/tableCellFormattingUtils';
import applyInlineStylesThroughoutContent from '../../../../../components/editor/customRichUtils/applyInlineStylesThroughoutContent';
import hyperFormulaInstance from '../../../manager/hyperFormulaInstance';

/**
 * Converts formula output into the display value Milanote expects for rendering.
 */
export const normaliseComputedCellValue = (
    hotComputedCellValue: HotComputedCellValue,
    locale: string,
    cellFormulaInfo: Partial<CellFormulaInfo> = {},
): HotComputedCellValue => {
    const { hfType, hfDetailedType } = cellFormulaInfo;

    if (
        isNumber(hotComputedCellValue) &&
        hfType === CellType.FORMULA &&
        (hfDetailedType === CellValueDetailedType.NUMBER_DATE ||
            hfDetailedType === CellValueDetailedType.NUMBER_DATETIME ||
            hfDetailedType === CellValueDetailedType.NUMBER_TIME)
    ) {
        const dateTime = hyperFormulaInstance.numberToDateTime(hotComputedCellValue);

        let year = 'year' in dateTime ? dateTime.year : undefined;
        let month = 'month' in dateTime ? dateTime.month : undefined;
        let day = 'day' in dateTime ? dateTime.day : undefined;
        const hours = 'hours' in dateTime ? dateTime.hours : undefined;
        const minutes = 'minutes' in dateTime ? dateTime.minutes : undefined;
        const seconds = 'seconds' in dateTime ? dateTime.seconds : undefined;

        if (hfDetailedType === CellValueDetailedType.NUMBER_TIME) {
            const today = new Date();
            day = today.getDate();
            month = today.getMonth() + 1;
            year = today.getFullYear();
        }

        if (year === undefined || month === undefined) return hotComputedCellValue;

        const date = new Date(year, month - 1, day, hours, minutes, seconds);

        const hasTime =
            hfDetailedType === CellValueDetailedType.NUMBER_DATETIME ||
            hfDetailedType === CellValueDetailedType.NUMBER_TIME;

        const dateTypeObject = {
            ...getDefaultTypeObject<DateTimeTypeObject>(CellTypeNames.DATE_TIME),
            [FormatOptions.HAS_TIME]: hasTime,
            [FormatOptions.HAS_SECONDS]: hasTime,
            [FormatOptions.TIME_FORMAT]: TimeStringFormatOptions.TWENTY_FOUR_HOUR_SECONDS,
            ...(hfDetailedType === CellValueDetailedType.NUMBER_TIME
                ? { [FormatOptions.DATE_FORMAT]: DateStringFormatOptions.NONE }
                : {}),
        };

        const numericDateTimeString = getNumericDateTimeStringFromCellValue(
            date.getTime().toString(),
            dateTypeObject,
            locale,
        );
        if (numericDateTimeString) return numericDateTimeString;
    }

    return hotComputedCellValue;
};

/**
 * Uses HyperFormula's computed value when HOT temporarily reports a formula cell's serialized source value.
 * I.e. The HOT value might say "=SUM(A1:A2)" when it should report the computed value, e.g. "2".
 *
 * This can happen during custom move snapshot undo/redo because Milanote updates HyperFormula and HOT separately,
 * unlike the standard edit flow where Handsontable's formulas plugin owns the full update.
 */
const getFormulaComputedCellValue = (cellFormulaInfo: Partial<CellFormulaInfo>): HotComputedCellValue | undefined => {
    const { hfCellValue, hfType, isFormula } = cellFormulaInfo;
    const hasFormulaCellType = isFormula || hfType === CellType.FORMULA || hfType === CellType.ARRAYFORMULA;

    if (!hasFormulaCellType) return;
    if (hfCellValue === undefined) return;
    if (typeof hfCellValue === 'object' && hfCellValue !== null) return;

    return hfCellValue;
};

/**
 * Returns the final string value that should be displayed for a cell.
 */
export const getCellRenderValue = (
    cellData: CellData,
    locale: string,
    hotComputedCellValue: HotComputedCellValue = null,
    cellFormulaInfo: Partial<CellFormulaInfo> = {},
): string => {
    const formulaComputedCellValue = getFormulaComputedCellValue(cellFormulaInfo);
    const rawCellValue = hotComputedCellValue ?? cellData.value;
    const value = formulaComputedCellValue !== undefined ? formulaComputedCellValue : rawCellValue;

    const normalisedComputedCellValue = normaliseComputedCellValue(value, locale, cellFormulaInfo);

    if (isNil(normalisedComputedCellValue)) return '';
    if (isBoolean(normalisedComputedCellValue)) return normalisedComputedCellValue.toString();

    return getRenderCellValueFromNormalisedComputedCellValue(cellData, locale, normalisedComputedCellValue).toString();
};

/**
 * Returns the Draft content used by the renderer for a cell.
 */
export const getCellRenderContentState = (
    cellData: CellData,
    locale: string,
    hotComputedCellValue?: HotComputedCellValue,
    cellFormulaInfo: Partial<CellFormulaInfo> = {},
): ContentState | null => {
    if (isNil(hotComputedCellValue) && !cellData.draftContent) return null;

    const contentState = cellData.draftContent
        ? convertFromRaw(cellData.draftContent)
        : ContentState.createFromText(getCellRenderValue(cellData, locale, hotComputedCellValue, cellFormulaInfo));

    return applyInlineStylesThroughoutContent(contentState, cellData.textStyle);
};
